import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons'
import { Col, Row, Statistic, Typography } from 'antd'
import { useCallback, useState } from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import { Link } from 'react-router-dom'
import { capitalize } from 'lodash'
import api from '../../api'
import { useAsync } from '../../hooks'
import { useIsAdmin, useCurrentUser } from '../../redux/slices/session'
import { toPath } from '../../routes'
import { ListError } from '../Common'
import Loading from '../Loading'
import ConnectivityRates from './ConnectivityRates'
import ConnectivityRatesChart from './ConnectivityRatesChart'
import DevicesSummary from './DevicesSummary'
import PatientCreationStats from './PatientCreationStats'
import ReportsStats from './ReportsStats'
import ChartTableSwitch from './ChartTableSwitch'

const { Title } = Typography

export default function Dashboard() {
  const { brandPrimary, brandSuccess, brandWarning } = useTheme()
  const classes = createUseStyles({
    title: {
      textAlign: 'center',
    },
    inlineTitle: {
      display: 'inline-block',
    },
    centeredRow: {
      textAlign: 'center',
    },
    alerts: {
      marginBottom: 10,
    },
    welcome: {
      fontWeight: '400 !important',
      color: '#000',
      fontSize: '2.6em',
      '& span': {
        fontWeight: 600,
      },
    },
    statisticContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '15px',
    },
    statistic: {
      '& .ant-statistic-title': {
        color: 'black',
      },
      background: 'white',
      textAlign: 'right',
      borderRadius: '10px',
      padding: '10px 15px',
      border: '1px solid transparent',
      display: 'inline-block',
    },
    statisticLink: {
      '& .ant-statistic, & .ant-statistic-title': {
        transition: 'all 0.1s ease',
      },
      '&:hover .ant-statistic': {
        borderColor: brandPrimary,
        borderWidth: '1px',
      },
      '&:hover .ant-statistic-title': {
        color: brandPrimary,
      },
    },
    card: {
      background: '#fff',
      padding: '10px 20px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.09)',
    },
    grid: {
      display: 'grid',
      gap: '20px',
      marginBottom: '30px',
      gridTemplateColumns: '1fr 1fr',
      gridTemplateRows: '1fr 1fr',
    }
  })()
  const isAdmin = useIsAdmin()
  const user = useCurrentUser()
  const fetchData = useCallback(() => api.dashboard.get(), [])
  const { value: stats, pending, error } = useAsync(fetchData)
  const [connectivityMode, setConnectivityMode] = useState('chart')

  if (pending) return <Loading large />
  if (error) {
    return (
      <ListError
        description={error.message}
        message="There was an error loading dashboard data"
      />
    )
  }

  return (
    <div>
      <Row style={{ marginBottom: 30 }} gutter={24}>
        <Col span={12}>
          <div className={classes.welcome}>
            Welcome{' '}
            <span style={{ color: brandPrimary }}>{capitalize(user.firstName)}</span>
          </div>
        </Col>
        <Col span={12}>
          <Row justify="end">
            <Col className={classes.statisticContainer}>
            <Link to={`${toPath.alerts()}?acknowledged=false`} className={classes.statisticLink} title='Go to alerts'>
              <Statistic
                className={classes.statistic}
                title="Alerts today"
                value={stats.alertsToday}
                prefix={stats.alertsToday
                  ? <WarningOutlined style={{ color: brandWarning }} />
                  : <CheckCircleOutlined style={{ color: brandSuccess }} />}
                valueStyle={{
                  color: stats.alertsToday ? brandWarning : brandSuccess,
                }}
              />
              </Link>
              <Link to={toPath.patients()} className={classes.statisticLink} title='Go to patients'>
                <Statistic
                  className={classes.statistic}
                  title="Total patients"
                  value={stats.totalPatients}
                />
              </Link>
              {isAdmin && (
                <Link to={toPath.practices()} className={classes.statisticLink} title='Go to practices'>
                  <Statistic
                    className={classes.statistic}
                    title="Total practices"
                    value={stats.totalPractices}
                  />
                </Link>
              )}
            </Col>
          </Row>
        </Col>
      </Row>

      <div className={classes.grid}>
        <div className={classes.card}>
          {stats?.devices && <DevicesSummary devicesStats={stats.devices} />}
        </div>

        <div className={classes.card}>
          {stats?.patientCreationStats && (
            <PatientCreationStats stats={stats.patientCreationStats} />
          )}
        </div>

        {!isAdmin && (
          <>
            {stats?.connectivityRates && (
              <div className={classes.card}>
                <Row justify="space-between" type="flex" align="middle">
                  <Col>
                    <Title level={3} className={classes.inlineTitle}>
                      Connectivity rates
                    </Title>
                  </Col>
                  <Col>
                   <ChartTableSwitch onChange={setConnectivityMode} viewMode={connectivityMode} />
                  </Col>
                </Row>

                {connectivityMode === 'chart' && (
                  <div>
                    <ConnectivityRatesChart
                      rates={stats.connectivityRates.reverse()}
                    />
                  </div>
                )}
                {connectivityMode === 'table' && (
                  <ConnectivityRates rates={stats.connectivityRates.reverse()} />
                )}
              </div>
            )}

            {stats?.reportsStats && (<div className={classes.card}><ReportsStats reports={stats.reportsStats} /></div>)}
          </>
      )}
      </div>
      <style>{`
        .ant-layout-content {
          background: transparent !important;
        }
      `}</style>
    </div>
  )
}
